import { useState, useRef, useEffect } from 'react';
import {
  Pencil,
  Trash2,
  Download,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Building2,
  ScrollText,
  FileText,
  Settings,
  AlertTriangle,
  X,
  MessageSquare,
  Sparkles,
  ChevronDown,
  GitFork,
  User,
  ArrowUpFromLine,
  ArrowDownToLine,
} from 'lucide-react';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import type { AgentSkill } from '../../../../api/agent-skills';
import { SkillFileManager } from './SkillFileManager';
import { getTagColor } from '../utils/tagColors';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface SkillDetailProps {
  skill: AgentSkill;
  onEdit: () => void;
  onDelete: () => void;
  onExport: () => void;
  onToggleEnabled: () => void;
  isDisabledByUser: boolean;
  onToggleUserDisabled: () => void;
  onForkTemplate?: () => void;
  onCopyToTenant?: () => void;
  onCopyToMySkills?: () => void;
  isToggleLoading?: boolean;
  isDeleteLoading?: boolean;
  isExportLoading?: boolean;
  isForkLoading?: boolean;
  isCopyToTenantLoading?: boolean;
  isCopyToMySkillsLoading?: boolean;
  actionError?: string | null;
  onDismissActionError?: () => void;
  isReadOnly?: boolean;
  isAdmin?: boolean;
  currentUserId?: string;
  onTryInChat?: () => void;
  onEditWithChat?: () => void;
}

export function SkillDetailSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1">
            <div className="h-6 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-2 flex gap-2">
              <div className="h-5 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-5 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function scopeMeta(t: TFunction<'pleri/skills'>, skill: AgentSkill) {
  if (skill.isBuiltIn) {
    return {
      icon: Sparkles,
      gradient: 'from-violet-500 to-purple-600',
      badge:
        'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
      label: t('pleri'),
    };
  }
  if (skill.scope === 'tenant') {
    return {
      icon: Building2,
      gradient: 'from-blue-500 to-indigo-600',
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      label: t('common:tenant'),
    };
  }
  if (skill.scope === 'template') {
    return {
      icon: GitFork,
      gradient: 'from-emerald-500 to-teal-600',
      badge:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      label: t('template'),
    };
  }
  return {
    icon: User,
    gradient: 'from-orange-400 to-amber-500',
    badge:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    label: t('mySkill'),
  };
}

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className="group flex items-center gap-2"
    >
      <div
        className={`relative h-5 w-9 rounded-full transition-colors duration-200 ${
          checked ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <div
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </div>
      <span className="text-xs font-medium text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200">
        {label}
      </span>
    </button>
  );
}

export function SkillDetail({
  skill,
  onEdit,
  onDelete,
  onExport,
  onToggleEnabled,
  isDisabledByUser,
  onToggleUserDisabled,
  onForkTemplate,
  onCopyToTenant,
  onCopyToMySkills,
  isDeleteLoading,
  isExportLoading,
  isForkLoading,
  isCopyToTenantLoading,
  isCopyToMySkillsLoading,
  actionError,
  onDismissActionError,
  isReadOnly,
  isAdmin,
  currentUserId,
  onTryInChat,
  onEditWithChat,
}: SkillDetailProps) {
  const { t } = useTranslation('pleri/skills');
  const [activeTab, setActiveTab] = useState<'instructions' | 'files' | 'details'>('instructions');
  const [showEditMenu, setShowEditMenu] = useState(false);
  const editMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showEditMenu) return;
    const handler = (e: MouseEvent) => {
      if (editMenuRef.current && !editMenuRef.current.contains(e.target as Node)) {
        setShowEditMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEditMenu]);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const descriptionIsLong =
    skill.description.split('\n').length > 3 || skill.description.length > 240;

  const isPleriSkill = skill.isBuiltIn;
  const isOrgSkill = skill.scope === 'tenant' && !skill.isBuiltIn;
  const isTemplateSkill = skill.scope === 'template';
  const isUserSkill = skill.scope === 'user';
  const isOwnTemplate = isTemplateSkill && currentUserId && skill.createdBy === currentUserId;

  const canEdit = isUserSkill || isOwnTemplate || (isOrgSkill && isAdmin);
  const canToggleEnabled = !isPleriSkill && !isTemplateSkill && !!canEdit;
  const showForkButton = isTemplateSkill;
  const canCopyToTenant =
    isUserSkill && !!isAdmin && currentUserId != null && skill.createdBy === currentUserId;
  const canCopyToMySkills = isOrgSkill;
  const showVisibilityToggle = isPleriSkill || (isOrgSkill && !isAdmin);

  const { icon: ScopeIcon, gradient, badge, label } = scopeMeta(t, skill);
  const tags = skill.tags ?? [];
  const creatorLine =
    (isTemplateSkill || isOrgSkill) && skill.creatorName
      ? `by ${currentUserId && skill.createdBy === currentUserId ? 'you' : skill.creatorName}`
      : null;

  return (
    <div className="flex h-full flex-col">
      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-10 flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-900">

        <div className="flex items-start gap-4">
          {/* Left: icon + title + tags */}
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-sm`}>
              <ScopeIcon className="h-5 w-5 text-white" />
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 leading-tight">
                {skill.displayName}
              </h2>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${badge}`}>
                  <ScopeIcon className="h-3 w-3" />
                  {label}
                  {creatorLine && <span className="font-normal opacity-75">{creatorLine}</span>}
                </span>

                {tags.map((tag) => {
                  const c = getTagColor(tag);
                  return (
                    <span key={tag} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${c.card}`}>
                      {tag}
                    </span>
                  );
                })}

                {(skill.metadata?.['_createdWith'] === 'chat' || skill.metadata?.['_editedWith'] === 'chat') && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-600 dark:bg-purple-900/20 dark:text-purple-300">
                    <Sparkles className="h-3 w-3" />
                    {t('aiAssisted')}
                  </span>
                )}

                {isDisabledByUser && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                    <EyeOff className="h-3 w-3" />
                    {t('hidden')}
                  </span>
                )}

                {isTemplateSkill && skill.forkCount != null && skill.forkCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <GitFork className="h-3 w-3" />
                    {t('forkCount', { count: skill.forkCount })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: action toolbar */}
          <div className="flex flex-shrink-0 items-center gap-2 pt-1">

            {/* On/Off toggle — clearly a switch, not a button */}
            {canToggleEnabled && (
              <>
                <ToggleSwitch
                  checked={skill.enabled}
                  onChange={onToggleEnabled}
                  label={skill.enabled ? t('common:on') : t('off')}
                />
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
              </>
            )}

            {/* Visibility — for Pleri / non-admin org */}
            {showVisibilityToggle && (
              <>
                <button
                  onClick={onToggleUserDisabled}
                  title={
                    isDisabledByUser
                      ? t('showInYourSessions')
                      : t('hideFromYourSessions')
                  }
                  className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    isDisabledByUser
                      ? 'border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                      : 'border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {isDisabledByUser ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {isDisabledByUser ? t('hidden') : t('visible')}
                </button>
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
              </>
            )}

            {/* Fork — template primary */}
            {showForkButton && onForkTemplate && (
              <button
                onClick={onForkTemplate}
                disabled={isForkLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
              >
                {isForkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitFork className="h-3.5 w-3.5" />}
                {isForkLoading ? t('forking') : t('fork')}
              </button>
            )}

            {/* Copy to tenant — admin-only action for own user skills */}
            {canCopyToTenant && onCopyToTenant && (
              <button
                onClick={onCopyToTenant}
                disabled={isCopyToTenantLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
              >
                {isCopyToTenantLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpFromLine className="h-3.5 w-3.5" />}
                {isCopyToTenantLoading ? t('copying') : t('copyToTenant')}
              </button>
            )}

            {/* Copy to my skills — available to any user on tenant skills */}
            {canCopyToMySkills && onCopyToMySkills && (
              <button
                onClick={onCopyToMySkills}
                disabled={isCopyToMySkillsLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-50 dark:border-orange-700 dark:bg-orange-950/30 dark:text-orange-300"
              >
                {isCopyToMySkillsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowDownToLine className="h-3.5 w-3.5" />}
                {isCopyToMySkillsLoading ? t('copying') : t('copyToMySkills')}
              </button>
            )}

            {/* Primary: Edit (editable skills only) */}
            {canEdit && (
              <div ref={editMenuRef} className="relative">
                <button
                  onClick={() => setShowEditMenu((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 active:bg-orange-700"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t('common:edit')}
                  <ChevronDown className="h-3 w-3 opacity-80" />
                </button>
                {showEditMenu && (
                  <div className="absolute top-full right-0 z-20 mt-1.5 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => { setShowEditMenu(false); onEdit(); }}
                      className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {t('editManually')}
                    </button>
                    {onEditWithChat && (
                      <button
                        type="button"
                        onClick={() => { setShowEditMenu(false); onEditWithChat(); }}
                        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm text-purple-700 hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-900/20"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        {t('editWithPleri')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Secondary: Try in chat */}
            {onTryInChat && (
              <button
                onClick={onTryInChat}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t('tryInChat')}
              </button>
            )}

            {/* Icon actions: Export + Delete — same border style as text buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={onExport}
                disabled={isExportLoading}
                title={t('exportSkill')}
                className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                {isExportLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              </button>

              {canEdit && (
                <button
                  onClick={onDelete}
                  disabled={isDeleteLoading}
                  title={t('deleteSkill')}
                  className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-500 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-red-800 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  {isDeleteLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          </div>
        </div>

        {actionError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{actionError}</span>
            {onDismissActionError && (
              <button onClick={onDismissActionError} className="flex-shrink-0 text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* ── Tabs ── */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
        <div className="flex px-6">
          {(
            [
              {
                id: 'instructions' as const,
                label: t('instructions'),
                icon: ScrollText,
              },
              {
                id: 'files' as const,
                label: skill.files?.length
                  ? t('filesWithCount', { length: skill.files.length })
                  : t('filesLabel'),
                icon: FileText,
              },
              {
                id: 'details' as const,
                label: t('common:details'),
                icon: Settings,
              },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`relative flex items-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors ${
                activeTab === id
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {activeTab === id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-orange-500" />
              )}
            </button>
          ))}
        </div>
      </div>
      {/* ── Tab content ── */}
      <div className={`flex-1 overflow-y-auto ${isDisabledByUser ? 'opacity-50' : ''}`}>
        {activeTab === 'instructions' && (
          <>
            <div className="border-b border-gray-100 px-6 py-4 dark:border-gray-800">
              <label className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                <Info className="h-3 w-3" />
                {t('common:description')}
              </label>
              <div className={`text-sm leading-relaxed text-gray-600 dark:text-gray-400 ${!descriptionExpanded && descriptionIsLong ? 'line-clamp-3' : ''}`}>
                <ChatMarkdown content={skill.description} />
              </div>
              {descriptionIsLong && (
                <button
                  type="button"
                  onClick={() => setDescriptionExpanded((v) => !v)}
                  className="mt-1 text-xs text-orange-600 underline-offset-2 hover:text-orange-700 hover:underline"
                >
                  {descriptionExpanded ? t('showLess') : t('showMore')}
                </button>
              )}
            </div>
            <div className="px-6 py-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ChatMarkdown content={skill.instructions} />
              </div>
            </div>
          </>
        )}

        {activeTab === 'files' && (
          <SkillFileManager skill={skill} readonly={!!isReadOnly || isPleriSkill} />
        )}

        {activeTab === 'details' && (
          <div className="space-y-5 px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {t('skillId')}
              </label>
              <code className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {skill.name}
              </code>
            </div>
            {skill.workspace && skill.workspace.length > 0 && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('workspaces')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {skill.workspace.map((ws) => (
                    <span key={ws} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {ws}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skill.compatibility && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('compatibility')}
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400">{skill.compatibility}</p>
              </div>
            )}
            {skill.license && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('license')}
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400">{skill.license}</p>
              </div>
            )}
            {skill.allowedTools && skill.allowedTools.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('allowedTools')}
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400">{skill.allowedTools.join(' ')}</p>
              </div>
            )}
            {skill.metadata && Object.entries(skill.metadata).some(([k]) => !k.startsWith('_')) && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {t('metadata')}
                </label>
                <div className="space-y-1">
                  {Object.entries(skill.metadata)
                    .filter(([k]) => !k.startsWith('_'))
                    .map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">{k}:</span> {v}
                      </p>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
